<template>
  <v-card class="filters-card" flat>

    <!-- Header -->
    <v-card-title class="filters-header" @click="toggleFiltros">
      <div class="d-flex align-center">
        <div class="icon-wrapper" :class="{ 'icon-active': filtrosExpandidos }">
          <v-icon size="20">
            {{ filtrosExpandidos ? 'mdi-filter-variant' : 'mdi-filter-variant-plus' }}
          </v-icon>
        </div>
        <div class="ml-3">
          <span class="filters-title">Filtros de Exportação</span>
          <div class="filters-subtitle" v-if="temFiltrosAtivos">
            {{ totalFiltrosAtivos }} filtro{{ totalFiltrosAtivos > 1 ? 's' : '' }} ativo{{ totalFiltrosAtivos > 1 ? 's' : '' }}
          </div>
        </div>
      </div>
      <v-btn text small @click.stop="toggleFiltros" class="collapse-btn" :disabled="loading">
        <v-icon small :class="{ 'rotate-icon': filtrosExpandidos }">mdi-chevron-down</v-icon>
        {{ filtrosExpandidos ? 'Recolher' : 'Expandir' }}
      </v-btn>
    </v-card-title>

    <!-- Campos -->
    <v-expand-transition>
      <div v-show="filtrosExpandidos">
        <v-divider />
        <v-card-text class="pa-6">
          <v-form ref="form">
            <v-row dense>

              <!-- Filiais -->
              <v-col cols="12" md="5">
                <div class="filter-field-wrapper">
                  <label class="field-label">
                    <v-icon small class="mr-1">mdi-domain</v-icon>
                    Filiais
                    <v-chip v-if="value.idfiliais.length > 0" x-small color="primary" text-color="white" class="ml-2 status-chip">
                      {{ value.idfiliais.length }}
                    </v-chip>
                  </label>
                  <v-autocomplete
                      autofocus
                      :items="filiaisFiltro"
                      clearable
                      item-text="filial"
                      item-value="idfilial"
                      outlined dense
                      hide-details="auto"
                      :disabled="loading"
                      :value="value.idfiliais"
                      @input="v => emit('idfiliais', v)"
                      multiple chips small-chips deletable-chips
                      :rules="[v => (v && v.length > 0) || 'Selecione ao menos uma filial']"
                      placeholder="Selecione as filiais"
                      class="custom-field"
                  >
                    <template v-slot:prepend-item>
                      <v-list-item ripple @click="toggleTodasFiliais" dense>
                        <v-list-item-action class="mr-3">
                          <v-icon :color="todasFiliaisSelecionadas ? '#023449' : ''" size="20">{{ iconTodasFiliais }}</v-icon>
                        </v-list-item-action>
                        <v-list-item-content>
                          <v-list-item-title class="text-body-2">
                            {{ todasFiliaisSelecionadas ? 'Desmarcar Todas' : 'Selecionar Todas' }}
                          </v-list-item-title>
                        </v-list-item-content>
                      </v-list-item>
                      <v-divider class="my-1" />
                    </template>
                    <template v-slot:selection="{ item, index }">
                      <v-chip v-if="index < 1" small>{{ item.filial }}</v-chip>
                      <span v-if="index === 1" class="grey--text text-caption">(+{{ value.idfiliais.length - 1 }} outras)</span>
                    </template>
                  </v-autocomplete>
                </div>
              </v-col>

              <!-- Tipos de Comissão -->
              <v-col cols="12" md="4">
                <div class="filter-field-wrapper">
                  <label class="field-label">
                    <v-icon small class="mr-1">mdi-cash-multiple</v-icon>
                    Tipos de Comissão
                    <v-chip v-if="value.tiposComissao.length > 0" x-small color="primary" text-color="white" class="ml-2 status-chip">
                      {{ value.tiposComissao.length }}
                    </v-chip>
                  </label>
                  <v-autocomplete
                      :items="tiposComissao"
                      clearable
                      item-text="texto"
                      item-value="valor"
                      outlined dense
                      hide-details="auto"
                      :disabled="loading"
                      :value="value.tiposComissao"
                      @input="v => emit('tiposComissao', v)"
                      multiple chips small-chips deletable-chips
                      :rules="[v => (v && v.length > 0) || 'Selecione ao menos um tipo']"
                      placeholder="Selecione os tipos"
                      class="custom-field"
                  >
                    <template v-slot:prepend-item>
                      <v-list-item ripple @click="toggleTodosTipos" dense>
                        <v-list-item-action class="mr-3">
                          <v-icon :color="todosTiposSelecionados ? '#023449' : ''" size="20">{{ iconTodosTipos }}</v-icon>
                        </v-list-item-action>
                        <v-list-item-content>
                          <v-list-item-title class="text-body-2">
                            {{ todosTiposSelecionados ? 'Desmarcar Todos' : 'Selecionar Todos' }}
                          </v-list-item-title>
                        </v-list-item-content>
                      </v-list-item>
                      <v-divider class="my-1" />
                    </template>
                    <template v-slot:selection="{ item, index }">
                      <v-chip v-if="index < 1" small>{{ item.texto }}</v-chip>
                      <span v-if="index === 1" class="grey--text text-caption">(+{{ value.tiposComissao.length - 1 }} outros)</span>
                    </template>
                    <template v-slot:item="{ item }">
                      <v-list-item-icon class="mr-3">
                        <v-icon color="#023449" size="18" class="mt-3">{{ item.icone }}</v-icon>
                      </v-list-item-icon>
                      <v-list-item-content>
                        <v-list-item-title class="text-body-2">{{ item.texto }}</v-list-item-title>
                        <v-list-item-subtitle class="text-caption">{{ item.descricao }}</v-list-item-subtitle>
                      </v-list-item-content>
                    </template>
                  </v-autocomplete>
                </div>
              </v-col>

              <!-- Mês de Referência -->
              <v-col cols="12" md="3">
                <div class="filter-field-wrapper">
                  <label class="field-label">
                    <v-icon small class="mr-1">mdi-calendar-month</v-icon>
                    Mês de Referência
                    <v-chip v-if="value.dtmes" x-small color="primary" text-color="white" class="ml-2 status-chip">
                      <v-icon x-small>mdi-check</v-icon>
                    </v-chip>
                  </label>
                  <div class="date-field-wrapper">
                    <InputDatePicker
                        :disabled="loading"
                        :value="value.dtmes"
                        @input="v => emit('dtmes', v)"
                        label=""
                        :outlined="true"
                        :dense="true"
                        type="mounth"
                    />
                  </div>
                </div>
              </v-col>

            </v-row>
          </v-form>
        </v-card-text>
      </div>
    </v-expand-transition>

    <v-divider />

    <!-- Summary + Ações -->
    <v-card-text class="pa-4">
      <div class="filters-summary">
        <div class="filters-summary-content">
          <div class="summary-header">
            <div class="d-flex align-center">
              <div class="summary-icon-wrapper">
                <v-icon small :color="temFiltrosAtivos ? '#10b981' : '#64748b'">
                  {{ temFiltrosAtivos ? 'mdi-filter-check' : 'mdi-filter-outline' }}
                </v-icon>
              </div>
              <span class="summary-title ml-2">
                {{ temFiltrosAtivos ? 'Filtros Selecionados:' : 'Nenhum Filtro Aplicado' }}
              </span>
            </div>

            <!-- Ações Desktop -->
            <div class="filters-summary-actions d-none d-sm-flex">
              <v-btn
                  v-if="temFiltrosAtivos"
                  small text color="error"
                  @click="$emit('limpar')"
                  :disabled="loading"
                  class="action-btn-clear"
              >
                <v-icon small left>mdi-filter-remove</v-icon>
                Limpar
              </v-btn>
              <v-btn
                  small depressed color="primary"
                  @click="gerar"
                  :loading="loading"
                  :disabled="loading || !temFiltrosAtivos"
                  class="action-btn-search"
              >
                <v-icon small left>mdi-file-download-outline</v-icon>
                Gerar
              </v-btn>
            </div>
          </div>

          <!-- Chips -->
          <v-expand-transition>
            <div v-show="temFiltrosAtivos" class="chips-container">
              <v-chip-group column class="chips-wrapper">
                <v-chip
                    v-if="value.idfiliais.length > 0"
                    small outlined color="#023449"
                    class="summary-chip" close
                    @click:close="emit('idfiliais', [])"
                >
                  <v-icon small left class="chip-icon">mdi-domain</v-icon>
                  <span class="chip-label">Filiais:</span>
                  <span class="chip-value">{{ filiaisChipLabel }}</span>
                </v-chip>

                <v-chip
                    v-if="value.tiposComissao.length > 0"
                    small outlined color="#023449"
                    class="summary-chip" close
                    @click:close="emit('tiposComissao', [])"
                >
                  <v-icon small left class="chip-icon">mdi-cash-multiple</v-icon>
                  <span class="chip-label">Tipos:</span>
                  <span class="chip-value">{{ tiposChipLabel }}</span>
                </v-chip>

                <v-chip
                    v-if="value.dtmes"
                    small outlined color="#023449"
                    class="summary-chip"
                >
                  <v-icon small left class="chip-icon">mdi-calendar-month</v-icon>
                  <span class="chip-label">Mês:</span>
                  <span class="chip-value">{{ mesFormatado }}</span>
                </v-chip>
              </v-chip-group>
            </div>
          </v-expand-transition>

          <!-- Ações Mobile -->
          <div class="filters-summary-actions-mobile d-flex d-sm-none">
            <v-btn
                v-if="temFiltrosAtivos"
                small text color="error"
                @click="$emit('limpar')"
                :disabled="loading"
                class="action-btn-clear flex-grow-1"
            >
              <v-icon small left>mdi-filter-remove</v-icon>
              Limpar
            </v-btn>
            <v-btn
                small depressed color="primary"
                @click="gerar"
                :loading="loading"
                :disabled="loading || !temFiltrosAtivos"
                class="action-btn-search flex-grow-1"
            >
              <v-icon small left>mdi-file-download-outline</v-icon>
              Gerar
            </v-btn>
          </div>
        </div>
      </div>
    </v-card-text>

  </v-card>
</template>

<script>
import InputDatePicker from "../../../Widgets/InputDatePicker.vue";

export default {
  name: "ExportadorFiltros",
  components: { InputDatePicker },

  props: {
    // Objeto com os valores dos filtros: { idfiliais, tiposComissao, dtmes }
    value: {
      type: Object,
      required: true,
    },
    filiaisFiltro: {
      type: Array,
      default: () => [],
    },
    tiposComissao: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      filtrosExpandidos: true,
    };
  },

  computed: {
    temFiltrosAtivos() {
      return this.value.idfiliais.length > 0 || this.value.tiposComissao.length > 0 || !!this.value.dtmes;
    },
    totalFiltrosAtivos() {
      return [
        this.value.idfiliais.length > 0,
        this.value.tiposComissao.length > 0,
        !!this.value.dtmes,
      ].filter(Boolean).length;
    },
    filiaisChipLabel() {
      const n = this.value.idfiliais.length;
      if (n === this.filiaisFiltro.length) return `Todas (${n})`;
      if (n <= 2) return this.value.idfiliais.map(id => this.filiaisFiltro.find(f => f.idfilial === id)?.filial).filter(Boolean).join(', ');
      return `${n} selecionadas`;
    },
    tiposChipLabel() {
      const n = this.value.tiposComissao.length;
      if (n === this.tiposComissao.length) return `Todos (${n})`;
      if (n <= 2) return this.value.tiposComissao.map(v => this.tiposComissao.find(t => t.valor === v)?.texto).filter(Boolean).join(', ');
      return `${n} selecionados`;
    },
    mesFormatado() {
      if (!this.value.dtmes) return '';
      const [ano, mes] = this.value.dtmes.split('-');
      return `${['Jan','Fev','Mar','Abr','Mai','Jun','Jul','Ago','Set','Out','Nov','Dez'][parseInt(mes) - 1]}/${ano}`;
    },
    todosTiposSelecionados()   { return this.value.tiposComissao.length === this.tiposComissao.length; },
    algumTipoSelecionado()     { return this.value.tiposComissao.length > 0 && !this.todosTiposSelecionados; },
    iconTodosTipos() {
      if (this.todosTiposSelecionados) return 'mdi-close-box';
      if (this.algumTipoSelecionado)   return 'mdi-minus-box';
      return 'mdi-checkbox-blank-outline';
    },
    todasFiliaisSelecionadas() { return this.value.idfiliais.length === this.filiaisFiltro.length; },
    algumaFiliaisSelecionada() { return this.value.idfiliais.length > 0 && !this.todasFiliaisSelecionadas; },
    iconTodasFiliais() {
      if (this.todasFiliaisSelecionadas) return 'mdi-close-box';
      if (this.algumaFiliaisSelecionada) return 'mdi-minus-box';
      return 'mdi-checkbox-blank-outline';
    },
  },

  methods: {
    toggleFiltros() {
      if (!this.loading) this.filtrosExpandidos = !this.filtrosExpandidos;
    },
    toggleTodosTipos() {
      this.$nextTick(() => {
        this.emit('tiposComissao', this.todosTiposSelecionados ? [] : this.tiposComissao.map(t => t.valor));
      });
    },
    toggleTodasFiliais() {
      this.$nextTick(() => {
        this.emit('idfiliais', this.todasFiliaisSelecionadas ? [] : this.filiaisFiltro.map(f => f.idfilial));
      });
    },
    emit(campo, valor) {
      this.$emit('input', { ...this.value, [campo]: valor });
    },
    gerar() {
      if (!this.$refs.form.validate()) return;
      this.$emit('gerar');
    },
  },
};
</script>

<style scoped>
.filters-card {
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(2, 52, 73, 0.08);
  border: 1px solid #e2e8f0;
  margin-bottom: 24px;
  overflow: hidden;
  transition: box-shadow 0.3s ease;
}

.filters-card:hover {
  box-shadow: 0 4px 12px rgba(2, 52, 73, 0.12);
  border-color: #cbd5e1;
}

.filters-header {
  cursor: pointer;
  user-select: none;
  display: flex; align-items: center; justify-content: space-between;
  padding: 16px 24px !important;
  position: relative;
  transition: background 0.2s ease;
}

.filters-header::before {
  content: '';
  position: absolute; left: 0; top: 0;
  height: 100%; width: 4px;
  background: linear-gradient(180deg, #023449 0%, #0369a1 100%);
  opacity: 0;
  transition: opacity 0.3s ease;
}

.filters-header:hover::before { opacity: 1; }
.filters-header:hover          { background-color: rgba(2, 52, 73, 0.02); }

.icon-wrapper {
  background: linear-gradient(135deg, rgba(2, 52, 73, 0.08) 0%, rgba(2, 52, 73, 0.04) 100%);
  border-radius: 10px; padding: 10px;
  display: flex; align-items: center; justify-content: center;
  box-shadow: 0 2px 6px rgba(2, 52, 73, 0.08);
  transition: all 0.3s ease;
}

.icon-wrapper >>> .v-icon { color: #023449 !important; }

.icon-active {
  background: linear-gradient(135deg, #023449 0%, #0369a1 100%);
  box-shadow: 0 4px 10px rgba(2, 52, 73, 0.2);
}

.icon-active >>> .v-icon { color: #ffffff !important; }

.filters-title {
  font-size: 1.063rem; font-weight: 700; color: #023449;
  letter-spacing: -0.01em; line-height: 1.2;
}

.filters-subtitle {
  font-size: 0.75rem; font-weight: 600; color: #10b981; margin-top: 2px;
}

.collapse-btn {
  text-transform: none !important; font-weight: 600 !important; letter-spacing: 0;
  border-radius: 8px; border: 2px solid #cbd5e1 !important;
  padding: 0 16px !important; height: 32px !important;
  background: transparent !important; transition: all 0.2s ease;
}

.collapse-btn:hover:not(.v-btn--disabled) {
  background: #023449 !important; border-color: #023449 !important;
  color: #ffffff !important; transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(2, 52, 73, 0.2);
}

.rotate-icon { transform: rotate(180deg); transition: transform 0.3s ease; }

.filter-field-wrapper { position: relative; }

.field-label {
  display: flex; align-items: center;
  font-size: 0.875rem; font-weight: 700; color: #023449;
  margin-bottom: 8px; letter-spacing: -0.01em; height: 24px;
}

.status-chip { height: 20px !important; font-size: 0.688rem; font-weight: 700; }

/* Campos — padrão SeletorFiliais */
.custom-field >>> .v-input__control { min-height: 44px !important; }

.custom-field >>> fieldset {
  border-color: #cbd5e1 !important;
  border-width: 2px !important;
  transition: all 0.2s ease;
}

.custom-field >>> .v-input__slot {
  background: #f8fafc !important;
  border-radius: 10px;
  transition: all 0.2s ease;
}

.custom-field >>> .v-input__slot:hover { background: #ffffff !important; }
.custom-field:hover >>> fieldset       { border-color: #023449 !important; }

.custom-field >>> .v-input--is-focused fieldset,
.custom-field.v-input--is-focused >>> fieldset {
  border-color: #023449 !important;
  border-width: 2px !important;
  box-shadow: 0 0 0 3px rgba(2, 52, 73, 0.1);
}

.custom-field >>> .v-input__prepend-inner .v-icon {
  color: #023449 !important; opacity: 0.7; transition: opacity 0.2s ease;
}

.custom-field:hover >>> .v-input__prepend-inner .v-icon { opacity: 1 !important; }

/* Campo Mês */
.date-field-wrapper >>> .v-input__control { min-height: 44px !important; }

.date-field-wrapper >>> fieldset {
  border-color: #cbd5e1 !important;
  border-width: 2px !important;
  transition: all 0.2s ease;
}

.date-field-wrapper >>> .v-input__slot {
  background: #f8fafc !important;
  border-radius: 10px !important;
  transition: all 0.2s ease;
}

.date-field-wrapper:hover >>> fieldset        { border-color: #023449 !important; }
.date-field-wrapper:hover >>> .v-input__slot  { background: #ffffff !important; }

.date-field-wrapper >>> .v-input--is-focused fieldset {
  border-color: #023449 !important;
  border-width: 2px !important;
  box-shadow: 0 0 0 3px rgba(2, 52, 73, 0.1);
}

.date-field-wrapper >>> .v-input--is-focused .v-input__slot { background: #ffffff !important; }

.date-field-wrapper >>> .v-input__prepend-inner .v-icon,
.date-field-wrapper >>> .v-input__append-inner .v-icon {
  color: #023449 !important; opacity: 0.7; transition: opacity 0.2s ease;
}

.date-field-wrapper:hover >>> .v-input__prepend-inner .v-icon,
.date-field-wrapper:hover >>> .v-input__append-inner .v-icon { opacity: 1 !important; }

/* Summary */
.filters-summary {
  background: linear-gradient(135deg, #f8fafc 0%, #ffffff 100%);
  border: 1px solid #e2e8f0; border-radius: 12px; padding: 16px;
  transition: all 0.3s ease;
}

.filters-summary:hover { border-color: #cbd5e1; box-shadow: 0 2px 8px rgba(2, 52, 73, 0.06); }

.filters-summary-content { display: flex; flex-direction: column; gap: 16px; }

.summary-header {
  display: flex; justify-content: space-between; align-items: center;
  flex-wrap: wrap; gap: 12px;
}

.summary-icon-wrapper {
  background: linear-gradient(135deg, rgba(2, 52, 73, 0.08) 0%, rgba(2, 52, 73, 0.04) 100%);
  border-radius: 8px; padding: 8px;
  display: flex; align-items: center; justify-content: center;
}

.summary-title { font-size: 0.875rem; font-weight: 700; color: #023449; letter-spacing: -0.01em; }

.chips-container { width: 100%; }
.chips-wrapper { margin: 0 !important; padding: 0 !important; }
.chips-wrapper >>> .v-slide-group__content { gap: 8px; flex-wrap: wrap; }

.filters-summary-actions { display: flex; gap: 8px; align-items: center; }
.filters-summary-actions-mobile { width: 100%; gap: 8px; margin-top: 4px; }

.action-btn-clear {
  text-transform: none !important; font-weight: 600 !important; letter-spacing: 0;
  border-radius: 8px; height: 32px !important; padding: 0 14px !important; transition: all 0.2s ease;
}

.action-btn-clear:hover:not(.v-btn--disabled) {
  background-color: rgba(220, 38, 38, 0.08) !important; transform: translateY(-1px);
}

.action-btn-search {
  text-transform: none !important; font-weight: 700 !important; letter-spacing: 0;
  border-radius: 8px; height: 32px !important; padding: 0 16px !important;
  transition: all 0.2s ease; box-shadow: 0 2px 8px rgba(2, 52, 73, 0.2) !important;
}

.action-btn-search:hover:not(.v-btn--disabled) {
  box-shadow: 0 4px 12px rgba(2, 52, 73, 0.3) !important; transform: translateY(-1px);
}

.summary-chip {
  border-width: 1.5px !important; font-weight: 600;
  height: 28px !important; border-radius: 8px; transition: all 0.2s ease;
}

.summary-chip:hover { background-color: rgba(2, 52, 73, 0.04) !important; }
.chip-icon  { margin-right: 4px; flex-shrink: 0; }
.chip-label { font-weight: 700; margin-right: 4px; font-size: 0.75rem; flex-shrink: 0; }
.chip-value { font-weight: 600; font-size: 0.75rem; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

/* Responsividade */
@media (max-width: 960px) {
  .filters-header { padding: 12px 16px !important; }
  .filters-title  { font-size: 1rem; }
  .summary-header { flex-direction: column; align-items: flex-start; }
}

@media (max-width: 599px) {
  .filters-card    { border-radius: 8px; margin-bottom: 16px; }
  .filters-header  { flex-wrap: wrap; gap: 12px; padding: 12px !important; }
  .collapse-btn    { width: 100%; height: 28px !important; font-size: 0.813rem; }
  .filters-summary { padding: 12px; border-radius: 8px; }
}

@media (prefers-reduced-motion: reduce) {
  *, *::before, *::after {
    animation-duration: 0.01ms !important;
    transition-duration: 0.01ms !important;
  }
}
</style>